/**
 * Routes for the two password screens a locked-out member reaches.
 *
 * These are deliberately new. The portal already has `/login` and
 * `/reset-password-form/:userUid/:token`, but both predate this work and are wired to
 * the enrollment backend rather than to Supabase — a Supabase reset link cannot land
 * on either of them.
 *
 * **The path shape is load-bearing, not cosmetic.** The mail hook that actually sends
 * the reset email parses the product and the line of business out of the *first two
 * path segments* of the redirect URL, and refuses to send anything at all when the
 * first segment is not a product it recognises. A friendlier-looking
 * `/auth/reset-password` would therefore produce no email and no error the member
 * could see. Keeping `/sleepio/wellness/...` in front is what makes the mail go out —
 * the same shape the existing magic-link callback already uses
 * (`/:product/:lob/activate/email/callback`).
 */

import { LOGIN_ORG_SLUG_PLACEHOLDER } from '@/utils/constants';

const PASSWORD_ROUTE_PRODUCT = 'sleepio';
const PASSWORD_ROUTE_LOB = 'wellness';

const PASSWORD_ROUTE_PREFIX = `/${PASSWORD_ROUTE_PRODUCT}/${PASSWORD_ROUTE_LOB}/auth`;

/** Where a member asks for a password link. */
export const FORGOT_PASSWORD_PATH = `${PASSWORD_ROUTE_PREFIX}/forgot-password`;

/** Where the emailed link lands. Registered at this literal path — see below. */
export const RESET_PASSWORD_PATH = `${PASSWORD_ROUTE_PREFIX}/reset-password`;

/**
 * The two paths above are also the route patterns registered in `src/index.tsx`.
 *
 * They are **literal**, not `/:product/:lob/...` patterns, and that is deliberate.
 * The portal serves more than one Sleepio product: the Canopy flow this work belongs
 * to, and Sleepio Cortex, which runs on an entirely different authentication backend.
 * A route with a product parameter in it would be reachable for every product on the
 * portal, which would offer a Supabase password screen to members whose accounts do
 * not live in Supabase. Spelling the product out keeps these two screens reachable
 * only for the product they were built for.
 */

/**
 * Absolute URL handed to Supabase as the reset link's destination.
 *
 * Note for whoever deploys this: the hosted Supabase project keeps its own list of
 * allowed redirect URLs, managed in the dashboard rather than in any repository. An
 * address that is not on that list is silently replaced with the project's site URL,
 * so this exact URL has to be added there per environment before reset links work.
 */
export const resetPasswordRedirectUrl = (redirectTo?: string | null): string => {
  // Carry the app's deep link through the email, because nothing else can.
  //
  // A member taps the link in their mail app, which opens their default browser — a
  // different browsing context from the one they asked for the reset in, with its own
  // empty session storage. Anything remembered on the way in is gone. If the address
  // the app is waiting on is not on the link itself, the member sets their password,
  // everything succeeds, and the app never hears back.
  //
  // Callers pass an address they have already validated.
  const base = `${window.location.origin}${RESET_PASSWORD_PATH}`;
  return redirectTo ? `${base}?redirectTo=${encodeURIComponent(redirectTo)}` : base;
};

/**
 * The Sleepio Canopy sign-in entry point.
 *
 * Named here so the screens that need to send a member back to sign in do not each
 * spell out the route. The organisation segment is a stand-in: this entry point never
 * reads it, and a member arriving from a reset link has no organisation in hand.
 */
export const SLEEPIO_CANOPY_LOGIN_PATH = `/activate/sleepio-canopy/wellness/${LOGIN_ORG_SLUG_PLACEHOLDER}/login`;

/** The final step, which mints the one-time hand-off code. */
export const SLEEPIO_CANOPY_LOGIN_COMPLETE_PATH = `${SLEEPIO_CANOPY_LOGIN_PATH}/complete`;
